import React, { Component } from 'react';
import { Tooltip } from '@trevor/ui-components';
import css from '../../css/module/draggable-list.css';

class DraggableList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: props.items, // copy to state because props doesn't always maintain order of hidden items (items below divider index)
      dragging: -1, // index of item being dragged
      dragStartX: 0, // where dragging started from
      dragStartY: 0,
      currentX: 0, // where dragging is currently at
      currentY: 0,
      showHidden: false,
      dividerIndex: props.visibleItems,
    };
    this._items = []; // the DOM elements for the items
  }

  moveItemTo(newPos, e) {
    const { dragging, items } = this.state;
    const currentPos = dragging;
    const itemsUpdated = [];
    const draggingItem = items[currentPos];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (i === currentPos) {
        continue; // we will add it later
      }
      if (i === newPos) {
        if (newPos > currentPos) {
          itemsUpdated.push(item);
        }
        itemsUpdated.push(draggingItem);
        if (newPos < currentPos) {
          itemsUpdated.push(item);
        }
      } else {
        itemsUpdated.push(item);
      }
    }
    const curRect = this.boundingRect(dragging);
    const newRect = this.boundingRect(newPos);

    const newDragStartY =
      this.state.dragStartY + this.state.currentY + (newRect.y - curRect.y);

    let { dividerIndex } = this.state;
    if (dragging < dividerIndex && newPos >= dividerIndex) {
      dividerIndex--;
    } else if (dragging >= dividerIndex && newPos < dividerIndex) {
      dividerIndex++;
    }

    this.setState({
      items: itemsUpdated,
      dragging: newPos,
      dragStartY: newDragStartY,
      currentY: e.clientY - newDragStartY,
      dividerIndex,
    });
    if (this.props.onChange) {
      // parent only cares about visible items
      this.props.onChange(itemsUpdated.slice(0, dividerIndex));
    }
  }

  boundingRect(i) {
    const el = this._items[i];
    const rect = el.getBoundingClientRect();
    return rect;
  }

  onMouseMove(e) {
    const { dragging, dividerIndex, items } = this.state;

    if (this.state.dragging > -1) {
      const isInside = (rect) =>
        rect.y < e.clientY &&
        rect.y + rect.height > e.clientY &&
        rect.x < e.clientX &&
        rect.x + rect.width > e.clientX;

      // check if have moved over another item
      for (let i = 0; i < items.length; i++) {
        if (i === this.state.dragging) {
          continue; // it will always be inside itself, so skip
        }
        if (isInside(this.boundingRect(i))) {
          // mouse is over this new position, so move it
          this.moveItemTo(i, e);
          return;
        }
      }
      // check if touching divider
      const dividerRect = this._divider.getBoundingClientRect();
      if (isInside(dividerRect)) {
        const curRect = this.boundingRect(dragging);
        const adjustment = dragging < dividerIndex ? -1 : 1;
        const newDragStartY =
          this.state.dragStartY +
          this.state.currentY +
          (dividerRect.y - curRect.y);
        const newDividerIndex = dividerIndex + adjustment;
        this.setState({
          dragStartY: newDragStartY,
          currentY: e.clientY - newDragStartY,
          dividerIndex: newDividerIndex,
        });
        if (this.props.onChange) {
          this.props.onChange(items.slice(0, newDividerIndex));
        }
        return;
      }

      const RANGE = 50;
      this.setState({
        currentX: Math.min(
          RANGE,
          Math.max(RANGE * -1, e.clientX - this.state.dragStartX),
        ),
        currentY: e.clientY - this.state.dragStartY,
      });
    }
  }

  onMouseDown(e, itemIndex) {
    this.setState({
      dragging: itemIndex,
      dragStartX: e.clientX,
      dragStartY: e.clientY,
      currentX: 0,
      currentY: 0,
    });
  }

  onMouseUp() {
    this.setState({
      dragging: -1,
    });
  }

  items() {
    const { dragging, dividerIndex, items } = this.state;

    const result = items.map((item, itemIndex) => {
      const style =
        dragging !== itemIndex
          ? {}
          : {
              position: 'relative',
              left: this.state.currentX,
              top: this.state.currentY,
            };
      return (
        <div
          key={
            item
          } /* must be something that changes when the item order changes */
          style={style}
          className={`${css.listItem} ${
            dragging === itemIndex ? css.dragging : ''
          } ${itemIndex >= dividerIndex ? css.hiddenItem : ''}`}
          onMouseDown={(e) => this.onMouseDown(e, itemIndex)}
          onTouchStart={(e) => this.onMouseDown(e.touches[0], itemIndex)}
          ref={(div) => (this._items[itemIndex] = div)}
        >
          {item}
        </div>
      );
    });
    const divider = (
      <div
        key="divider"
        className={css.divider}
        ref={(div) => (this._divider = div)}
      >
        <span style={{ width: '10px' }} />
        <span className={css.line} />
        <span className={css.tooltip}>
          <span data-tooltip-id="draggableInfo">
            <div className={css.questionMark} aria-hidden="true">
              ?
            </div>
          </span>
          <Tooltip id="draggableInfo">
            Move fields above this line to show them in the map
          </Tooltip>
        </span>
      </div>
    );
    return [
      ...result.slice(0, this.state.dividerIndex),
      divider,
      ...result.slice(this.state.dividerIndex),
    ];
  }

  render() {
    return (
      <div
        className={css.draggableList}
        ref={(div) => (this._container = div)}
        onTouchEnd={(e) => this.onMouseUp(e.touches[0])}
        onMouseUp={(e) => this.onMouseUp(e)}
        onTouchMove={(e) => this.onMouseMove(e.touches[0])}
        onMouseMove={(e) => this.onMouseMove(e)}
      >
        {this.items()}
      </div>
    );
  }
}

export default DraggableList;
